import { useCallback, useRef, useState } from "react";
import { persistReturningUser } from "../utils/returningUser.js";
import { t } from "../utils/translate.js";
// PLA-380. Previously drove a homegrown `window.open` flow that posted
// the auth code back via `postMessage` and exchanged it server-side
// through the `authSocialLoginCallback` GraphQL mutation. That flow
// raced KC's session-cookie lifecycle on the post-logout retry path and
// produced KC's generic "Something went wrong" page intermittently
// (Levente's 2026-05-26 HAR on the PLA-378 branch deploy reproduced it).
//
// The hook is now a thin wrapper around `config.onSocialLogin(provider)`,
// which the app implements by calling `userManager.signinPopup(...)`. KC
// session cookies + PKCE state are managed natively by oidc-client-ts,
// the popup callback at `/popup-callback.html` does the postMessage
// handshake, and the code-for-token exchange runs browser↔KC directly.
//
// `api` is kept in the signature for source-compat with the existing
// callers — it's intentionally unused now.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useSocialLogin(_api, config) {
    const [error, setError] = useState(null);
    // Bugbot MED #5 (PR #1949). In-flight guard against concurrent popup
    // launches. The pre-PLA-380 hook used a `cleanupRef` that closed the
    // previous popup before opening a new one; the signinPopup refactor
    // dropped that. Without this guard, rapid double-clicks on
    // "Continue with Google" can start overlapping `signinPopup` flows
    // that race oidc-client-ts's PKCE/state handling — the same class of
    // intermittent KC failure this PR targets. Reject re-entry while a
    // popup is already in flight; the user can retry once the first
    // attempt resolves.
    const inFlightRef = useRef(false);
    const reportError = useCallback((next)=>{
        setError(next);
        config.onLoginError?.(next);
    }, [
        config
    ]);
    const clearError = useCallback(()=>setError(null), []);
    const login = useCallback(async (provider)=>{
        if (inFlightRef.current) {
            return;
        }
        inFlightRef.current = true;
        setError(null);
        try {
            const tokens = await config.onSocialLogin(provider);
            persistReturningUser(tokens);
            config.onLoginSuccess(tokens);
        } catch (err) {
            // Bugbot MED (PR #1949). KC's rate-limit response shares the
            // `access_denied` error code with deliberate user-driven aborts.
            // Discriminate by the description first (carries "too many
            // requests" / "try again in …" / "rate limit" language) so
            // legitimate rate-limit failures still surface as RATE_LIMITED
            // banners. Mirrors the original `mapPopupError` precedence from
            // the pre-PLA-380 `socialLoginError.ts`.
            if (isRateLimitError(err)) {
                // Banner copy: prefer the OAuth `error_description` (KC's
                // explanation), fall back to `err.message`, then a translated
                // default. Display-only fallback is fine here — classification
                // already happened above.
                const desc = errorDescriptionField(err);
                const fallback = err instanceof Error && err.message ? err.message : undefined;
                reportError({
                    code: 'RATE_LIMITED',
                    message: desc || fallback || t(config, 'auth.error.rateLimited')
                });
                return;
            }
            // Deliberate user cancellations (closed popup, denied IdP consent)
            // surface from oidc-client-ts as errors with a recognisable
            // message / `error` code. Suppress them rather than render a red
            // error banner the user didn't ask for — they already know they
            // cancelled.
            if (isUserCancellation(err)) {
                return;
            }
            reportError({
                code: 'UNKNOWN',
                message: err instanceof Error && err.message ? err.message : t(config, 'auth.error.socialLoginFailed')
            });
        } finally{
            inFlightRef.current = false;
        }
    }, [
        config,
        reportError
    ]);
    return {
        login,
        error,
        clearError
    };
}
// `oidc-client-ts` exposes the raw OAuth2 error code as the thrown
// Error's `error` field and the human-readable explanation as
// `error_description`. Pull them off safely for the heuristics below.
function errorCode(err) {
    if (err && typeof err === 'object' && 'error' in err) {
        const code = err.error;
        return typeof code === 'string' ? code : undefined;
    }
    return undefined;
}
// Returns ONLY the OAuth `error_description` field (or undefined). Does
// NOT fall back to `err.message` — that conflation caused Bugbot MED #12
// on PR #1949: oidc-client-ts rejections whose `.message` incidentally
// contained "try again in" or "too many" got misclassified as rate-limit
// errors. Mirrors the independent-field pattern used by
// `isPopupCancellation` in `useAuthConfig.ts`.
function errorDescriptionField(err) {
    if (err && typeof err === 'object') {
        const desc = err.error_description;
        if (typeof desc === 'string' && desc.length > 0) return desc;
    }
    return undefined;
}
const RATE_LIMIT_CODES = new Set([
    'temporarily_unavailable',
    'too_many_requests'
]);
const RATE_LIMIT_DESC_RE = /too many|try again in|rate.?limit/i;
// Classify against the structured OAuth signals only — never against
// `err.message`. Matches the pre-PLA-380 `mapPopupError` behaviour and
// closes Bugbot MED #12 (PR #1949): conflating message with description
// caused random oidc-client-ts errors whose `.message` mentioned "try
// again" or "too many" to be misclassified as rate-limit.
function isRateLimitError(err) {
    const code = errorCode(err);
    if (code && RATE_LIMIT_CODES.has(code)) return true;
    const desc = errorDescriptionField(err);
    return !!desc && RATE_LIMIT_DESC_RE.test(desc);
}
// `oidc-client-ts`'s `signinPopup` rejects with `Error('Popup closed by user')`
// when the user closes the popup before completion, or with an
// `OidcError` carrying `error === 'access_denied'` when the IdP rejects.
// Both are "user cancelled" signals — but only AFTER we've ruled out
// `isRateLimitError` (KC's rate-limit responses reuse `access_denied`).
function isUserCancellation(err) {
    if (!err || !(err instanceof Error)) return false;
    if (/popup closed/i.test(err.message)) return true;
    const code = errorCode(err);
    return code === 'access_denied' || code === 'cancelled';
}

//# sourceMappingURL=useSocialLogin.js.map